import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { SharedComponentsModule } from "../../../shared/module/shared-components.module";
import { ModalButtons } from "../../../shared/component/modal-container/modal-container.component";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { LocalDate } from "../../../../core/pojo/date-variable";
import { MetricDataRole, MetricDataRoleMap } from "../../dto/MetricData";
import { UUID } from "../../../../core/pojo/id-variable";
import { MetricBulkRequestCompanyWrapper } from "../../dto/metric-bulk-request";
import { MetricSharedLinkWrapper, SharedLinkPeriods } from "../../dto/MetricSharedLinkWrapper";
import { takeUntil } from "rxjs/operators";
import { firstValueFrom, Subject } from "rxjs";
import { MetricSharedLinkService } from "../../service/backend/metric-shared-link-service";

@Component({
  selector: 'app-kpi-modal-bulk-msl',
  standalone: true,
  imports: [
    SharedComponentsModule,
  ],
  templateUrl: './kpi-modal-bulk-msl.component.html',
  styleUrl: './kpi-modal-bulk-msl.component.scss',
})
export class KpiModalBulkMslComponent implements OnChanges, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();

  protected readonly MetricDataRoleMap = MetricDataRoleMap;

  @Input({ required: true }) companyWrappers: MetricBulkRequestCompanyWrapper[];
  @Input({ required: true }) uniquePeriods: LocalDate[];
  @Input({ required: true }) orgUUID: UUID;
  @Output() closeEmitter = new EventEmitter<boolean>();

  form: MslFormGroup;
  periods = new SharedLinkPeriods();
  numberOfCompaniesMslWillBeApplied: number;
  modalButtons: ModalButtons = {
    close: true,
    dismiss: 'Dismiss',
    apply: 'Apply',
  };

  showModalDialog = false;
  title = '';

  constructor(private sharedLinkService: MetricSharedLinkService) {
  }

  ngOnChanges(): void {
    this.initVariables();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
  }

  async apply(): Promise<void> {
    const filteredCompanyWrappers = this.filterCompanyWrappers();
    for (let i = 0; i < filteredCompanyWrappers.length; i++) {
      const companyWrapper = filteredCompanyWrappers[i];
      const mslFormValue = companyWrapper.linkForm.getFormRawValue();
      this.setFromTillValues(mslFormValue, this.form);

      await firstValueFrom(this.sharedLinkService.update(mslFormValue));
      this.title = `Updating ${(Math.floor((i / filteredCompanyWrappers.length) * 100))} out of 100%`;
    }

    this.closeEmitter.emit(true);
  }

  private setFromTillValues(mslFormValue: MetricSharedLinkWrapper, form: MslFormGroup): void {
    const formValue = form.value;
    if (formValue.role === MetricDataRole.ACTUAL) {
      mslFormValue.actual.isActivated = true;
      mslFormValue.actual.fromPeriod = formValue.from;
      mslFormValue.actual.tillPeriod = formValue.till;
    }

    if (formValue.role === MetricDataRole.BUDGET) {
      mslFormValue.budget.isActivated = true;
      mslFormValue.budget.fromPeriod = formValue.from;
      mslFormValue.budget.tillPeriod = formValue.till;
    }
  }

  /**
   * Provides the company wrappers, which:
   * 1) has the msl. Bulk msl works only if there is an msl. There is a separate functionality for turning the msl on.
   */
  private filterCompanyWrappers(): MetricBulkRequestCompanyWrapper[] {
    return this.companyWrappers.filter((companyWrapper) => {
      return Boolean(companyWrapper.linkForm.getFormRawValue().uuid);
    });
  }

  private initVariables(): void {
    this.initForm();
    this.initPeriods();
    this.initCompaniesNumber();
  }

  private initForm(): void {
    this.form = new FormGroup({
      from: new FormControl<LocalDate>(null, { validators: [Validators.required] }),
      till: new FormControl<LocalDate>(null, { validators: [Validators.required] }),
      role: new FormControl<MetricDataRole>(MetricDataRole.ACTUAL, { validators: [Validators.required] }),
    });
    this.subscribeOnFormChanges();
  }

  private initCompaniesNumber(): void {
    this.numberOfCompaniesMslWillBeApplied = this.companyWrappers.filter((companyWrapper) =>
      companyWrapper.linkForm.getFormRawValue().uuid // msl exists
    ).length;
  }

  private initPeriods(): void {
    if (!this.companyWrappers || !this.uniquePeriods) {
      this.periods = {
        from: [],
        till: [],
        all: [],
      };
      return;
    }
    const periods = this.uniquePeriods;
    this.periods = {
      all: periods,
      till: periods.slice().reverse(),
      from: periods.slice().reverse(),
    };
  }

  private subscribeOnFormChanges(): void {
    // on new from selected -> all till periods must at least as big as from.
    this.form.controls.from.valueChanges.pipe(takeUntil(this._destroyStream))
      .subscribe((selectedFromPeriod) => {
        // .reverse doesn't change original array, due to .filter present before it
        this.periods.till = this.periods.all.filter((period) => period >= selectedFromPeriod).reverse();
      });

    // on new till selected -> all from periods must be smaller or equal as till.
    this.form.controls.till.valueChanges.pipe(takeUntil(this._destroyStream))
      .subscribe((selectedTillPeriod) => {
        this.periods.from = this.periods.all.filter((period) => period <= selectedTillPeriod);
      });

    // update variable of companies amount which will be updated.
    this.form.controls.role.valueChanges.pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        this.initCompaniesNumber();
      });
  }
}

type MslFormGroup = FormGroup<{
  from: FormControl<LocalDate>,
  till: FormControl<LocalDate>,
  role: FormControl<MetricDataRole>,
}>
